import { useApolloClient } from "@apollo/client";
import { sortByKey } from "@notemeal/utils/sort";
import { Workbook } from "exceljs";
import saveAs from "file-saver";
import React, { useState } from "react";
import TablePage from "../../../../components/universal/TablePage";
import {
  AthletesForTeamProfilesExportDocument,
  AthletesForTeamProfilesExportQuery,
  AthletesForTeamProfilesExportQueryVariables,
  FoodLogNotificationSettings,
  NamedTagForSelectionFragment,
  useAthletesForTeamEngagementLazyQuery,
  useAthletesForTeamFoodPreferencesLazyQuery,
} from "../../../../types";
import { PaginationHooks } from "../../../../utils/pagination";
import { getAthleteMobileState } from "../../../Athlete/Summary/AccountManagement/utils";
import AthleteEngagement from "./EngagementRows";
import { exportColumns as engagementColumns, serializeRows as serializeEngagementRows } from "./Export/Engagement";
import { exportColumns as preferenceColumns, serializeRows as serializePreferenceRows } from "./Export/Preferences";
import { exportColumns as profileColumns, serializeRows as serializeProfileRows } from "./Export/Profiles";
import AthletePreferenceRows from "./PreferenceRows";
import { AthletesTableHeaderRow, AthletesTableRow } from "./ProfileRow";
import TableToolbar, { AVOIDED_FOODS, ENGAGEMENT, PREFERENCES, PROFILES, SortType, TableFilter, TableView } from "./Toolbar";
import {
  IAthleteEngagementRowInput,
  IAthletePreferenceRowInput,
  IAthleteProfileRowInput,
  athletesToEngagementRows,
  athletesToPreferenceRows,
  athletesToProfileRows,
  getAthleteFilter,
} from "./utils";

export interface EnableAthlete {
  phoneNumber: string | null;
  email: string | null;
  id: string;
  activationLinkSent: string | null;
  orgMembership: { id: string; isActive: boolean } | null;
}

export interface AthleteTableProps {
  teamId: string;
  displayEnableAccountModal: (athlete: EnableAthlete) => void;
  displaySubscribeToAthleteModal: (args: { athleteNotificationSettings: FoodLogNotificationSettings | null; athleteId: string }) => void;
  teamFoodLogNotificationSettings: FoodLogNotificationSettings | null;
  filter: TableFilter;
  setFilter: (filter: TableFilter) => void;
  paginationHooks: PaginationHooks;
  rows: IAthleteProfileRowInput[];
  loading: boolean;
  total: number;
  sort: SortType | null;
  setSort: (s: SortType | null) => void;
  isNotemealLinked: boolean;
  selectedNamedTags: NamedTagForSelectionFragment[];
  onChangeNamedTags: (newTags: NamedTagForSelectionFragment[]) => void;
  onCreateAthlete: () => void;
  onEditAthlete: (athlete: IAthleteProfileRowInput) => void;
}

export const AthleteTable = ({
  teamId,
  displaySubscribeToAthleteModal,
  displayEnableAccountModal,
  teamFoodLogNotificationSettings,
  paginationHooks,
  rows,
  loading,
  total,
  filter,
  setFilter,
  sort,
  setSort,
  isNotemealLinked,
  selectedNamedTags,
  onChangeNamedTags,
  onCreateAthlete,
  onEditAthlete,
}: AthleteTableProps) => {
  const { query, queryText, onChangeQueryText, resetOffset } = paginationHooks;
  const [inLastDays, setInLastDays] = useState(7);
  const apolloClient = useApolloClient();

  const tagIds = selectedNamedTags.map(({ tag: { id } }) => id);

  const queryProps = {
    query,
    inLastDays,
    filter: getAthleteFilter(filter),
    teamId,
    tagIds,
    priorityTagIds: tagIds,
  };

  const [engagementRows, setEngagementRows] = useState<IAthleteEngagementRowInput[]>([]);

  const [preferenceRows, setPreferenceRows] = useState<IAthletePreferenceRowInput[]>([]);

  const handleSetEngagementRows = (rows: IAthleteEngagementRowInput[], _sort: SortType | null) => {
    if (_sort === null || _sort === AVOIDED_FOODS) {
      setEngagementRows(sortByKey(rows, "lastName").concat());
    } else {
      setEngagementRows(sortByKey(rows, _sort, { reverse: true }).concat());
    }
  };

  const handleSetPreferenceRows = (rows: IAthletePreferenceRowInput[], _sort: SortType | null) => {
    const sortedRows =
      _sort === AVOIDED_FOODS
        ? [...rows.filter(a => a.dislikedFoodGroupNames.length > 0), ...rows.filter(a => !(a.dislikedFoodGroupNames.length > 0))]
        : sortByKey(rows, "lastName").concat();
    setPreferenceRows(sortedRows);
  };

  const handleSort = (sort: SortType | null) => {
    setSort(sort);
    handleSetPreferenceRows(preferenceRows, sort);
    handleSetEngagementRows(engagementRows, sort);
  };

  const [view, setView] = useState<TableView>(PROFILES);

  const [athletesForTeamEngagement, { data: engagementData, loading: engagementLoading }] = useAthletesForTeamEngagementLazyQuery({
    variables: queryProps,
    onCompleted: data => {
      handleSetEngagementRows(athletesToEngagementRows(data.athletesForTeam), sort);
    },
  });

  const handleSetInLastDays = (inLastDays: number) => {
    setInLastDays(inLastDays);
    athletesForTeamEngagement({ variables: { ...queryProps, inLastDays } });
  };

  const [athletesForTeamPreference, { data: preferenceData, loading: preferenceLoading }] = useAthletesForTeamFoodPreferencesLazyQuery({
    variables: queryProps,
    onCompleted: data => {
      handleSetPreferenceRows(athletesToPreferenceRows(data.athletesForTeam), sort);
    },
  });

  const handleSetFilter = (_filter: TableFilter) => {
    const newQueryProps = {
      ...queryProps,
      filter: getAthleteFilter(_filter),
    };
    if (engagementData) {
      athletesForTeamEngagement({
        variables: newQueryProps,
      });
    }
    if (preferenceData) {
      athletesForTeamPreference({ variables: newQueryProps });
    }
    setFilter(_filter);
    resetOffset();
  };

  const handleClickEnableAccount = (athlete: EnableAthlete) => {
    getAthleteMobileState(athlete) !== "active" && displayEnableAccountModal(athlete);
  };

  const handleSetView = (view: TableView) => {
    if (view === ENGAGEMENT && !engagementData) {
      athletesForTeamEngagement({ variables: queryProps });
    } else if (view === PREFERENCES && !preferenceData) {
      athletesForTeamPreference({ variables: queryProps });
    }
    setView(view);
    resetOffset();
  };

  async function handleExport() {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet();
    worksheet.getRow(1).alignment = {
      horizontal: "center",
      vertical: "middle",
    };

    if (view === PROFILES) {
      worksheet.columns = profileColumns;
      worksheet.name = "AthleteProfiles";
      // use what we got if it isn't paginated
      if (rows.length !== total) {
        const { data } = await apolloClient.query<AthletesForTeamProfilesExportQuery, AthletesForTeamProfilesExportQueryVariables>({
          query: AthletesForTeamProfilesExportDocument,
          variables: queryProps,
        });
        const unpaginatedRows = athletesToProfileRows(data.athletesForTeam);
        worksheet.addRows(serializeProfileRows(unpaginatedRows));
      } else {
        worksheet.addRows(serializeProfileRows(rows));
      }
    } else if (view === ENGAGEMENT) {
      worksheet.columns = engagementColumns;
      worksheet.name = "AthleteEngagement";
      worksheet.addRows(serializeEngagementRows(engagementRows));
    } else if (view === PREFERENCES) {
      worksheet.columns = preferenceColumns;
      worksheet.addRows(serializePreferenceRows(preferenceRows));
      worksheet.name = "AthleteFoodPreferences";
    }
    const toExport = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([toExport]), `${worksheet.name}.xls`);
  }

  const baseRowProps = {
    onClickEnableAccount: handleClickEnableAccount,
    displaySubscribeToAthleteModal: displaySubscribeToAthleteModal,
    teamFoodLogNotificationSettings: teamFoodLogNotificationSettings,
  };

  return (
    <TablePage
      tableHeaderRow={<AthletesTableHeaderRow
        view={view}
        sort={sort}
        setSort={handleSort} />}
      header={
        <TableToolbar
          title="Roster"
          inLastDays={inLastDays}
          setInLastDays={handleSetInLastDays}
          setView={handleSetView}
          view={view}
          filter={filter}
          setFilter={handleSetFilter}
          queryText={queryText}
          setQueryText={onChangeQueryText}
          onClickAdd={onCreateAthlete}
          onExport={handleExport}
          selectedNamedTags={selectedNamedTags}
          onChangeNamedTags={onChangeNamedTags}
          isNotemealLinked={isNotemealLinked}
        />
      }
      paginationHooks={paginationHooks}
      loading={loading || preferenceLoading || engagementLoading}
      total={total}
      tableBodyRows={
        !!rows ? (
          view === ENGAGEMENT ? (
            <AthleteEngagement
              {...baseRowProps}
              rows={engagementRows}
              paginationHooks={paginationHooks}
              isNotemealLinked={isNotemealLinked}
            />
          ) : view === PREFERENCES ? (
            <AthletePreferenceRows
              {...baseRowProps}
              rows={preferenceRows}
              paginationHooks={paginationHooks}
              isNotemealLinked={isNotemealLinked}
            />
          ) : (
            rows.map(row => (
              <AthletesTableRow
                key={row.id}
                setEditing={() => onEditAthlete(row)}
                row={row}
                view={view}
                isNotemealLinked={isNotemealLinked}
                {...baseRowProps}
              />
            ))
          )
        ) : null
      }
    />
  );
};
